import { Component, computed, inject, input, signal, Signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiClientConstant } from '@cureskin/api-client';
import { InstantCheckup, RegimenClass } from '@cureskin/api-client/src/server-api';
import { CsIconModule } from '@components/icon';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CompleteProfileSection } from '@services/complete-your-profile-service';
import { EventLoggerService } from '@services/event-logger-service';

interface CompleteProfileSlot {
  type: string;
  checkup: InstantCheckup | null;
  placeholderImage: string;
}

/** Hero artwork for one (regimen class × gender) pair. `mirrored` flips the source so every hero faces the same way. */
interface CompleteProfileHeroArt {
  src: string;
  mirrored: boolean;
}

/** One rendered hero layer. All layers stay mounted and cross-fade on `active`, so switching tabs never re-fetches. */
export interface CompleteProfileHero extends CompleteProfileHeroArt {
  regimenClass: RegimenClass;
  active: boolean;
}

enum TakePhotoSource {
  CTA = 'cta',
  SLOT = 'slot',
  BANNER_BG = 'banner_bg',
}

@Component({
  selector: 'complete-your-profile',
  templateUrl: './complete-your-profile.html',
  standalone: true,
  imports: [CsIconModule, ButtonCtaComponent],
})
export class CompleteYourProfileComponent {
  readonly sections = input<CompleteProfileSection[]>([]);
  readonly gender = input<string>('');

  private readonly router = inject(Router);
  private readonly eventLogger = inject(EventLoggerService);

  readonly regimenClassEnum = ApiClientConstant.Regimen.Class;
  readonly checkupType = ApiClientConstant.InstantCheckup.Type;
  /** Hero artwork per regimen class, keyed by gender — a male user sees a male model, a female user a female one. */
  private readonly heroByClass: Record<string, Record<string, CompleteProfileHeroArt>> = {
    [ApiClientConstant.Regimen.Class.FACE]: {
      [ApiClientConstant.User.Gender.FEMALE]: { src: 'https://cdn.cureskin.com/app/img/face-scan-hero.png', mirrored: true },
      [ApiClientConstant.User.Gender.MALE]: { src: 'https://cdn.cureskin.com/app/img/male-face-ai.png', mirrored: false },
    },
    [ApiClientConstant.Regimen.Class.HAIR]: {
      [ApiClientConstant.User.Gender.FEMALE]: { src: 'https://cdn.cureskin.com/app/img/female-hair-ai.png', mirrored: false },
      [ApiClientConstant.User.Gender.MALE]: { src: 'https://cdn.cureskin.com/app/img/hair-scan-hero.png', mirrored: false },
    },
  };
  readonly scanFrameImage: string = '/assets/images/complete-profile/scan-frame.svg';
  readonly aiAnalysisIcon: string = 'https://cdn.cureskin.com/app/img/ai_analysis_icon.svg';
  readonly checkIcon: string = 'circle_tick_filled';
  readonly placeholderImageByType: Record<string, string> = {
    [ApiClientConstant.InstantCheckup.Type.FRONT_FACE]: '/assets/images/face_scan_front.png',
    [ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE]: '/assets/images/face_scan_left.png',
    [ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE]: '/assets/images/face_scan_right.png',
    [ApiClientConstant.InstantCheckup.Type.HAIR_FRONT]: '/assets/images/face_scan_hair_front.jpg',
    [ApiClientConstant.InstantCheckup.Type.HAIR_TOP]: '/assets/images/face_scan_top.jpg',
  };

  private readonly selectedClass: WritableSignal<RegimenClass | null> = signal(null);

  readonly activeClass: Signal<RegimenClass | null> = computed((): RegimenClass | null => {
    const sections = this.sections();
    const selected = this.selectedClass();
    if (selected && sections.some((section: CompleteProfileSection): boolean => section.regimenClass === selected)) {
      return selected;
    }
    return sections[0]?.regimenClass ?? null;
  });

  readonly slots: Signal<CompleteProfileSlot[]> = computed((): CompleteProfileSlot[] => {
    const activeClass = this.activeClass();
    const activeSection = this.sections().find(
      (section: CompleteProfileSection): boolean => section.regimenClass === activeClass);
    const checkupsByType = activeSection?.checkupsByType ?? {};
    return Object.entries(checkupsByType)
      .map(([type, checkup]: [string, InstantCheckup | null]): CompleteProfileSlot => ({
        type,
        checkup,
        placeholderImage: this.placeholderImageByType[type] ?? this.scanFrameImage,
      }))
      // Uploaded (imagePath) cards first, empty slots after — stable within each group.
      .sort((a: CompleteProfileSlot, b: CompleteProfileSlot): number =>
        Number(!!b.checkup) - Number(!!a.checkup));
  });

  readonly hasAnyCheckup: Signal<boolean> = computed((): boolean =>
    this.slots().some((slot: CompleteProfileSlot): boolean => !!slot.checkup));

  /** Anything other than an explicit Male (unset, 'None', 'Both') falls back to the female artwork. */
  private readonly heroGender: Signal<string> = computed((): string =>
    (this.gender() === ApiClientConstant.User.Gender.MALE
      ? ApiClientConstant.User.Gender.MALE
      : ApiClientConstant.User.Gender.FEMALE));

  /** One layer per tab, all rendered at once — a tab switch only flips `active`, so no image is fetched mid-transition. */
  readonly heroes: Signal<CompleteProfileHero[]> = computed((): CompleteProfileHero[] => {
    const gender = this.heroGender();
    const activeClass = this.activeClass();
    return this.sections()
      .filter((section: CompleteProfileSection): boolean => !!this.heroByClass[section.regimenClass]?.[gender])
      .map((section: CompleteProfileSection): CompleteProfileHero => ({
        ...this.heroByClass[section.regimenClass][gender],
        regimenClass: section.regimenClass,
        active: section.regimenClass === activeClass,
      }));
  });

  readonly isFaceActive: Signal<boolean> = computed((): boolean =>
    this.activeClass() === ApiClientConstant.Regimen.Class.FACE);

  readonly firstMissingType: Signal<string | null> = computed((): string | null =>
    this.slots().find((slot: CompleteProfileSlot): boolean => !slot.checkup)?.type ?? null);

  selectClass(event: Event, regimenClass: RegimenClass): void {
    event.stopPropagation();
    this.selectedClass.set(regimenClass);
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'complete_your_profile_tab_switch', regimenClass }));
  }

  handleBannerClick(): void {
    this.navigateForActiveSection(TakePhotoSource.BANNER_BG);
  }

  handleCtaClick(event: Event): void {
    event.stopPropagation();
    this.navigateForActiveSection(TakePhotoSource.CTA);
  }

  private navigateForActiveSection(source: TakePhotoSource): void {
    const missingType = this.firstMissingType();
    if (!missingType) {
      this.router.navigate(['/user/instantCheckup']);
      return;
    }
    this.navigateToTakePhoto(missingType, source);
  }

  handleSlotClick(event: Event, slot: CompleteProfileSlot): void {
    event.stopPropagation();
    if (slot.checkup) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
        name: 'complete_your_profile_view_checkup', type: slot.type, checkupId: slot.checkup.objectId,
      }));
      this.router.navigate([`/user/instantCheckup/${slot.checkup.objectId}`]);
      return;
    }
    this.navigateToTakePhoto(slot.type, TakePhotoSource.SLOT);
  }

  private navigateToTakePhoto(type: string, source: TakePhotoSource): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'complete_your_profile_take_photo', type, source }));
    this.router.navigate(['/user/instantCheckup/preview'], { queryParams: { tag: type } });
  }
}
